import React, { useMemo, useState } from "react";
import {
  buildHangarLinkExport, parseHangarLinkImport,
  computeMergeDelta, computeReplaceDelta,
  applyMerge, applyReplace, downloadJsonFile,
} from "./fleetExport";
import { getShipStatus, SHIP_STATUS, SHIP_STATUS_LABELS } from "./FleetFilterBar";

const ACCENT = "#ce93d8";

// ─── Shared modal frame ──────────────────────────────────────────────────────
function ModalFrame({ title, subtitle, onClose, children, maxWidth = 720 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: "#0a0d18", border: `1px solid ${ACCENT}44`, padding: 24, maxWidth, width: "100%", maxHeight: "88vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2.5, color: ACCENT, textTransform: "uppercase", marginBottom: 4 }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.55)", marginBottom: 16 }}>{subtitle}</div>
        )}
        {children}
      </div>
    </div>
  );
}

// ─── EXPORT MODAL ────────────────────────────────────────────────────────────
export function FleetExportModal({ open, onClose, currentShips, scShips, handle }) {
  // Mass-select bucket: all / pledged / in_game. Switching the bucket flips
  // all matching ships' check state, then the user can fine-tune individually.
  const [statusBucket, setStatusBucket] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set((currentShips || []).map(e => e.ship_id)));

  const shipsById = useMemo(() => {
    const m = new Map();
    (scShips || []).forEach(s => m.set(s.id, s));
    return m;
  }, [scShips]);

  // Entry rows enriched with the catalog ship so the picker can render names.
  const rows = useMemo(() => {
    return (currentShips || []).map(e => ({ ...e, ship: shipsById.get(e.ship_id) })).filter(r => r.ship);
  }, [currentShips, shipsById]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (q) {
        const hay = `${r.ship.name} ${r.ship.manufacturer || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search]);

  function applyBucket(bucket) {
    setStatusBucket(bucket);
    setSelected(() => {
      const next = new Set();
      for (const r of rows) {
        if (bucket === "all") { next.add(r.ship_id); }
        else if (getShipStatus(r) === bucket) { next.add(r.ship_id); }
      }
      return next;
    });
  }

  function toggle(shipId) {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(shipId)) n.delete(shipId); else n.add(shipId);
      return n;
    });
  }

  function doExport() {
    const entries = rows.filter(r => selected.has(r.ship_id));
    const json = buildHangarLinkExport(entries, shipsById, handle);
    const totalHulls = entries.reduce((s, r) => s + (r.quantity || 1), 0);
    const fname = `starkeeper-fleet-${(handle || "export").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.json`;
    downloadJsonFile(json, fname);
    onClose?.({ exported: entries.length, hulls: totalHulls });
  }

  if (!open) return null;

  const buckets = [
    { key: "all", label: "All", count: rows.length },
    { key: SHIP_STATUS.PLEDGED, label: SHIP_STATUS_LABELS.pledged, count: rows.filter(r => getShipStatus(r) === SHIP_STATUS.PLEDGED).length },
    { key: SHIP_STATUS.IN_GAME, label: SHIP_STATUS_LABELS.in_game, count: rows.filter(r => getShipStatus(r) === SHIP_STATUS.IN_GAME).length },
  ];
  const selectedCount = filteredRows.filter(r => selected.has(r.ship_id)).length;

  return (
    <ModalFrame
      title="Export Fleet → Hangar.link"
      subtitle="Generates a hangar.link-compatible JSON file. Upload it at hangar.link/files/import to visualise your fleet there."
      onClose={() => onClose?.(null)}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {buckets.map(b => (
          <button key={b.key} type="button" onClick={() => applyBucket(b.key)}
            style={{
              padding: "8px 14px",
              fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
              background: statusBucket === b.key ? `${ACCENT}20` : "transparent",
              border: `1px solid ${statusBucket === b.key ? ACCENT : "rgba(255,255,255,0.15)"}`,
              color: statusBucket === b.key ? ACCENT : "rgba(232,234,246,0.55)",
              cursor: "pointer",
            }}>
            Select {b.label} ({b.count})
          </button>
        ))}
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter…"
          style={{ flex: "1 1 200px", padding: "8px 12px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
      </div>

      <div style={{ border: "1px solid rgba(255,255,255,0.08)", maxHeight: 360, overflowY: "auto", marginBottom: 12 }}>
        {filteredRows.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.35)" }}>NO SHIPS</div>
        ) : filteredRows.map(r => {
          const checked = selected.has(r.ship_id);
          const status = getShipStatus(r);
          const statusColor = status === SHIP_STATUS.IN_GAME ? "#34d399" : "#4fc3f7";
          return (
            <label key={r.ship_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", background: checked ? "rgba(206,147,216,0.04)" : "transparent" }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(r.ship_id)} />
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6", flex: 1 }}>
                {r.ship.name} <span style={{ color: "rgba(232,234,246,0.4)", fontSize: 12 }}>· {r.ship.manufacturer}</span>
              </span>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: statusColor, border: `1px solid ${statusColor}55`, padding: "1px 6px", textTransform: "uppercase" }}>
                {status === SHIP_STATUS.IN_GAME ? "IN-GAME" : "PLEDGED"}
              </span>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, color: ACCENT, minWidth: 28, textAlign: "right" }}>×{r.quantity || 1}</span>
            </label>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
          {selectedCount} of {filteredRows.length} shown selected
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => onClose?.(null)}
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: "8px 16px", background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(232,234,246,0.7)", textTransform: "uppercase", cursor: "pointer" }}>Cancel</button>
          <button type="button" onClick={doExport} disabled={selected.size === 0}
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: "8px 16px", background: `${ACCENT}20`, border: `1px solid ${ACCENT}`, color: ACCENT, textTransform: "uppercase", cursor: selected.size === 0 ? "not-allowed" : "pointer", opacity: selected.size === 0 ? 0.5 : 1 }}>
            Download JSON ({selected.size})
          </button>
        </div>
      </div>
    </ModalFrame>
  );
}

// ─── IMPORT MODAL ────────────────────────────────────────────────────────────
export function FleetImportModal({ open, onClose, currentShips, scShips, onApply }) {
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState(null);
  const [mode, setMode] = useState(null); // null | 'merge' | 'replace'

  function handleFile(file) {
    if (!file) return;
    setFilename(file.name);
    setError(null);
    setParsed(null);
    setMode(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        const result = parseHangarLinkImport(json, scShips);
        if (result.error) { setError(result.error); return; }
        setParsed(result);
      } catch (e) {
        setError(`Couldn't parse JSON: ${e?.message || e}`);
      }
    };
    reader.onerror = () => setError("Failed to read file");
    reader.readAsText(file);
  }

  const merge = useMemo(() => parsed && computeMergeDelta(currentShips, parsed.matched), [parsed, currentShips]);
  const replace = useMemo(() => parsed && computeReplaceDelta(currentShips, parsed.matched), [parsed, currentShips]);

  function confirm() {
    if (!parsed || !mode) return;
    const next = mode === "merge"
      ? applyMerge(currentShips, parsed.matched)
      : applyReplace(parsed.matched);
    onApply?.(next, mode);
    reset();
    onClose?.();
  }

  function reset() {
    setParsed(null); setError(null); setFilename(null); setMode(null);
  }

  if (!open) return null;

  return (
    <ModalFrame
      title="Import Fleet ← Hangar.link / Fleet Canvas"
      subtitle="Accepts both formats: hangar.link/fleet/canvas Save-Canvas JSON (starjumpFleetviewer), and the SC-Open Hangar Export Chrome extension's RSI pledge export (hangarexport). Auto-detected on upload."
      onClose={() => { reset(); onClose?.(); }}
      maxWidth={760}
    >
      {/* File picker */}
      <div style={{ padding: "14px 16px", border: "1px dashed rgba(255,255,255,0.18)", marginBottom: 12 }}>
        <input type="file" accept="application/json,.json" onChange={e => handleFile(e.target.files?.[0])} style={{ color: "rgba(232,234,246,0.65)", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
        {filename && (
          <div style={{ marginTop: 6, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: "rgba(232,234,246,0.45)" }}>
            Loaded: {filename}
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: "10px 12px", border: "1px solid rgba(239,83,80,0.3)", background: "rgba(239,83,80,0.05)", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#ef5350", marginBottom: 12 }}>
          {error}
        </div>
      )}

      {parsed && (
        <>
          {/* Parse summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
            <StatBox label="Pledges" value={parsed.totals.pledges} />
            <StatBox label="Ships matched" value={parsed.matched.length} color="#34d399" />
            <StatBox label="Unmatched" value={parsed.unmatched.length} color={parsed.unmatched.length > 0 ? "#ef5350" : undefined} />
          </div>

          <div style={{ marginBottom: 12, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.55)" }}>
            Detected format: <span style={{ color: ACCENT }}>{parsed.source}</span>{parsed.handle ? ` · handle "${parsed.handle}"` : ""}
          </div>

          {/* Mode picker — side-by-side cards with per-mode summary */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <ModeCard
              active={mode === "merge"}
              onClick={() => setMode("merge")}
              title="Merge"
              subtitle="Add quantities onto your current fleet"
              color="#34d399"
              changes={merge && [
                merge.added.length > 0 && `+ Add ${merge.added.length} new ship type${merge.added.length === 1 ? "" : "s"}`,
                merge.increased.length > 0 && `↑ Increase quantity on ${merge.increased.length} existing ship${merge.increased.length === 1 ? "" : "s"}`,
                (merge.added.length === 0 && merge.increased.length === 0) && "No changes (all already present at this quantity)",
              ].filter(Boolean)}
            />
            <ModeCard
              active={mode === "replace"}
              onClick={() => setMode("replace")}
              title="Replace"
              subtitle="Discard current fleet, set to the import exactly"
              color="#ef5350"
              changes={replace && [
                replace.removed.length > 0 && `× Remove ${replace.removed.length} ship type${replace.removed.length === 1 ? "" : "s"} from current fleet`,
                replace.added.length > 0 && `+ Add ${replace.added.length} new ship type${replace.added.length === 1 ? "" : "s"}`,
                replace.changed.length > 0 && `↔ Change quantity on ${replace.changed.length} ship${replace.changed.length === 1 ? "" : "s"}`,
                replace.kept.length > 0 && `= Keep ${replace.kept.length} ship${replace.kept.length === 1 ? "" : "s"} unchanged`,
              ].filter(Boolean)}
              danger
            />
          </div>

          {/* Optional collapsibles for matched / unmatched lists */}
          {parsed.matched.length > 0 && (
            <details style={{ marginBottom: 8 }}>
              <summary style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "#34d399", cursor: "pointer", textTransform: "uppercase" }}>
                Matched ships ({parsed.matched.length})
              </summary>
              <div style={{ padding: "8px 12px", maxHeight: 200, overflowY: "auto", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.7)" }}>
                {parsed.matched.map(m => <div key={m.ship_id}>{m.name} ×{m.quantity}</div>)}
              </div>
            </details>
          )}
          {parsed.unmatched.length > 0 && (
            <details style={{ marginBottom: 12 }}>
              <summary style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "#ef5350", cursor: "pointer", textTransform: "uppercase" }}>
                Unmatched ships ({parsed.unmatched.length}) — not in our catalog
              </summary>
              <div style={{ padding: "8px 12px", maxHeight: 200, overflowY: "auto", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.55)" }}>
                {parsed.unmatched.map((u, i) => <div key={i}>{u.title} ×{u.quantity}{u.kinds.length > 0 ? ` (${u.kinds.join(", ")})` : ""}</div>)}
              </div>
            </details>
          )}
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button type="button" onClick={() => { reset(); onClose?.(); }}
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: "8px 16px", background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(232,234,246,0.7)", textTransform: "uppercase", cursor: "pointer" }}>Cancel</button>
        <button type="button" onClick={confirm} disabled={!parsed || !mode || parsed.matched.length === 0}
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, padding: "8px 16px", background: mode === "replace" ? "rgba(239,83,80,0.15)" : `${ACCENT}20`, border: `1px solid ${mode === "replace" ? "#ef5350" : ACCENT}`, color: mode === "replace" ? "#ef5350" : ACCENT, textTransform: "uppercase", cursor: (!parsed || !mode) ? "not-allowed" : "pointer", opacity: (!parsed || !mode) ? 0.5 : 1 }}>
          {mode === "replace" ? "Confirm Replace" : mode === "merge" ? "Confirm Merge" : "Pick a mode"}
        </button>
      </div>
    </ModalFrame>
  );
}

function StatBox({ label, value, color = "rgba(232,234,246,0.8)" }) {
  return (
    <div style={{ padding: 10, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color }}>{value}</div>
    </div>
  );
}

function ModeCard({ active, onClick, title, subtitle, color, changes, danger }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        textAlign: "left", padding: "12px 14px",
        background: active ? `${color}14` : "transparent",
        border: `1px solid ${active ? color : "rgba(255,255,255,0.1)"}`,
        cursor: "pointer",
      }}>
      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.55)", marginBottom: 8 }}>{subtitle}</div>
      {changes && changes.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 16, fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: danger ? "rgba(255,200,200,0.8)" : "rgba(200,255,200,0.8)" }}>
          {changes.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </button>
  );
}
